// v2 Dashboard Home — Living Cast overview.
// KPIs, recent cast members (AI char thumbs), voice pool preview,
// wall shareables, OBS live strip URL. Read-only views; mutations live on
// /dashboard/settings, /dashboard/overlay, /dashboard/factions.

import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../../lib/session";
import { api, getMe, type MeResponse } from "../../lib/api";
import { aiCharImage, normHandle } from "../../lib/cast";
import { voiceForHandle } from "../../lib/voices";
import "./dashboard.css";

interface ProfileResp {
  factions: Array<{ id: string; slug: string; name: string; color: string; total: number }>;
  donations: Array<{
    id: string;
    donor_name: string;
    donor_handle?: string;
    amount: number;
    message: string | null;
    faction_id: string | null;
    created_at: number;
  }>;
  stats: {
    total_raised: number;
    supporter_count: number;
    top_donor: string | null;
    donation_count: number;
  };
  streamer: {
    handle: string;
    display_name: string;
    avatar_url: string | null;
    bio: string | null;
    overlay_key: string;
    platform: "twitch" | "youtube" | "unknown";
  };
}

function CopyButton({ text, label = "Copy", variant = "default" }: { text: string; label?: string; variant?: "default" | "bits" }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`dash-btn${variant === "bits" ? " dash-btn-bits" : ""}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          const ta = document.createElement("textarea");
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand("copy"); } catch { /* noop */ }
          ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? "✓ Copied" : label}
    </button>
  );
}

export default function DashboardHome(): JSX.Element {
  const sessionUser = useSession((s) => s.user);
  const sessionLoading = useSession((s) => s.loading);
  const reloadSession = useSession((s) => s.reload);

  const meQ = useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: 30_000,
  });
  const user = meQ.data?.user ?? sessionUser;

  // Auth gate — match the other /dashboard/* routes. If we've finished
  // loading both the session bootstrap and the /api/me query and there's
  // still no user, bounce to /login. Without this, unauth visitors briefly
  // see the Studio shell instead of being redirected.
  if (!sessionLoading && !meQ.isLoading && !user) {
    return <Navigate to="/login?returnUrl=/dashboard" replace />;
  }
  const meStreamer = meQ.data?.streamer ?? null;
  const isCreator = !!user?.is_creator;

  const [upgrading, setUpgrading] = useState(false);
  const [upgradeErr, setUpgradeErr] = useState<string | null>(null);
  async function becomeCreator(): Promise<void> {
    setUpgrading(true);
    setUpgradeErr(null);
    try {
      const res = await fetch("/api/me/become-creator", { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reloadSession();
      await meQ.refetch();
    } catch (e) {
      setUpgradeErr((e as Error).message);
    } finally {
      setUpgrading(false);
    }
  }

  const profileQ = useQuery({
    queryKey: ["dashboard-overview", user?.handle],
    queryFn: () => api.get<ProfileResp>(`/api/streamer/${user?.handle}`),
    enabled: !!user?.handle && isCreator,
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "https://subswall.com";
  const handle = user?.handle ?? "";
  const overlayKey = meStreamer?.overlay_key ?? "";
  const overlayUrl = overlayKey ? `${origin}/overlay/?key=${overlayKey}` : "";
  const publicUrl = handle ? `${origin}/${handle}` : "";

  // ---- KPIs ------------------------------------------------------------------
  const donations = profileQ.data?.donations ?? [];
  const weekMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const weekDonations = donations.filter((d) => d.created_at >= weekMs);
  const weekTotal = weekDonations.reduce((s, d) => s + d.amount, 0);
  const lifetime = profileQ.data?.stats.total_raised ?? 0;
  const supporterCount = profileQ.data?.stats.supporter_count ?? 0;
  const avgDepth = supporterCount > 0
    ? Math.round((profileQ.data?.stats.donation_count ?? 0) / supporterCount * 10) / 10
    : 0;

  // ---- Recent cast — unique supporters, last 6 -------------------------------
  const recentCast = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ key: string; name: string; handle: string; amount: number }> = [];
    const sorted = [...donations].sort((a, b) => b.created_at - a.created_at);
    for (const d of sorted) {
      const h = normHandle(d.donor_handle ?? d.donor_name);
      if (seen.has(h)) continue;
      seen.add(h);
      out.push({ key: h, name: d.donor_name, handle: h, amount: d.amount });
      if (out.length >= 6) break;
    }
    return out;
  }, [donations]);

  // ---- Voice preview — 5 random recent supporters ----------------------------
  const voicePreview = useMemo(() => {
    const seen = new Set<string>();
    const picks: Array<{ name: string; handle: string }> = [];
    for (const d of donations) {
      const h = normHandle(d.donor_handle ?? d.donor_name);
      if (seen.has(h)) continue;
      seen.add(h);
      picks.push({ name: d.donor_name, handle: h });
    }
    // Stable deterministic shuffle: keep first 5 supporters (most recent) so
    // the preview is meaningful, not random per render.
    return picks.slice(0, 5);
  }, [donations]);

  // ---- Share intents ---------------------------------------------------------
  const tweetText = encodeURIComponent(
    "I'm collecting tips on @subswall — every supporter gets their own AI character + voice. Drop a totem on my wall.",
  );
  const tweetIntent = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(publicUrl)}`;
  const discordShareText = `Drop a totem on my wall — every supporter gets a permanent AI cast member: ${publicUrl}`;

  // ---- Donor view (default) --------------------------------------------------
  if (!isCreator) {
    return (
      <>
        <div className="dash-page-head">
          <div>
            <h1 className="dash-page-title">Welcome{user?.display_name ? `, ${user.display_name}` : ""}.</h1>
            <p className="dash-page-sub">Donor account · supporter wallet</p>
          </div>
        </div>

        <div className="dash-card elev">
          <p style={{ color: "var(--text-sec)", margin: "0 0 14px 0", lineHeight: 1.55 }}>
            You're set up as a donor. Discover a streamer and drop a totem — your handle gets a permanent
            AI character + voice on their Living Cast.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href="/" className="dash-btn dash-btn-pri">Discover streamers</a>
            <a href="/dashboard/settings" className="dash-btn">Edit profile</a>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">Stream Yourself?</h2>
          </div>
          <p style={{ color: "var(--text-sec)", margin: "0 0 14px 0", fontSize: 13, lineHeight: 1.6 }}>
            Turn this account into a creator account to get your own Forever Wall, an OBS overlay URL,
            and a public profile at <code style={{ fontFamily: "var(--f-mono)" }}>subswall.com/{user?.handle ?? "you"}</code>.
            Free and reversible.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="dash-btn dash-btn-pri" onClick={becomeCreator} disabled={upgrading}>
              {upgrading ? "Working…" : "Become a creator →"}
            </button>
            {upgradeErr ? <span style={{ color: "var(--danger, #ff5252)" }}>{upgradeErr}</span> : null}
          </div>
        </div>
      </>
    );
  }

  // ---- Creator view ----------------------------------------------------------
  return (
    <>
      <div className="dash-page-head">
        <div>
          <h1 className="dash-page-title">Living Cast · {user?.display_name ?? handle}</h1>
          <p className="dash-page-sub">@{handle} · v2 active</p>
        </div>
        {publicUrl ? (
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="dash-btn dash-btn-pri">
            View public wall ↗
          </a>
        ) : null}
      </div>

      {/* KPIs */}
      <div className="dash-kpis">
        <div className="dash-kpi gold">
          <div className="l">Lifetime tipped</div>
          <div className="v">${lifetime.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="s">{profileQ.data?.stats.donation_count ?? 0} totems · permanent</div>
        </div>
        <div className="dash-kpi purple">
          <div className="l">Supporters</div>
          <div className="v">{supporterCount.toLocaleString()}</div>
          <div className="s">unique characters in your cast</div>
        </div>
        <div className="dash-kpi green">
          <div className="l">This week</div>
          <div className="v">${weekTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="s">{weekDonations.length} tips · last 7d</div>
        </div>
        <div className="dash-kpi blue">
          <div className="l">Avg memory depth</div>
          <div className="v">{avgDepth || 0}</div>
          <div className="s">tips per supporter</div>
        </div>
      </div>

      {/* Recent cast members */}
      <div className="dash-card elev">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Recent cast members</h2>
          <span className="dash-card-sub">last 6 supporters · AI Ultra characters</span>
        </div>
        {recentCast.length === 0 ? (
          <p style={{ color: "var(--text-mute)", margin: 0 }}>
            No supporters yet. Share your wall link to land your first cast member.
          </p>
        ) : (
          <div className="dash-cast-grid">
            {recentCast.map((c) => (
              <div key={c.key} className="dash-cast">
                <div className="dash-cast-img-wrap">
                  <img
                    className="dash-cast-img"
                    src={aiCharImage(c.handle)}
                    alt={c.name}
                    loading="lazy"
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.style.display = "none";
                    }}
                  />
                </div>
                <div className="dash-cast-name">{c.name}</div>
                <div className="dash-cast-amt">${c.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Voice pool preview */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Voice pool assignments</h2>
          <span className="dash-card-sub">deterministic · 50-voice ElevenLabs pool</span>
        </div>
        {voicePreview.length === 0 ? (
          <p style={{ color: "var(--text-mute)", margin: 0 }}>Voice assignments appear once you have supporters.</p>
        ) : (
          <div className="dash-voice-list">
            {voicePreview.map((p) => {
              const v = voiceForHandle(p.handle);
              return (
                <div key={p.handle} className="dash-voice-row">
                  <div className="dash-voice-icon">{p.name[0]?.toUpperCase() ?? "?"}</div>
                  <div>
                    <div className="dash-voice-donor">{p.name}</div>
                    <div className="dash-voice-name">{v.name} · {v.vibe}</div>
                  </div>
                  <span className="dash-voice-vibe">{v.gender === "n" ? "neutral" : v.gender === "f" ? "fem" : "masc"}</span>
                  <span className="dash-voice-vibe">locked</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Wall shareables */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Wall shareables</h2>
          <span className="dash-card-sub">spread the cast</span>
        </div>
        <div className="dash-share-row" style={{ marginBottom: 10 }}>
          <code>{publicUrl || "—"}</code>
          <CopyButton text={publicUrl} label="Copy URL" />
        </div>
        <div className="dash-share-row">
          <a href={tweetIntent} target="_blank" rel="noopener noreferrer" className="dash-btn">
            Share on X
          </a>
          <CopyButton text={discordShareText} label="Copy for Discord" />
          <CopyButton text={publicUrl} label="Copy link" variant="bits" />
        </div>
      </div>

      {/* OBS Live Strip */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">OBS Live Strip URL</h2>
          <span className="dash-card-sub">private · paste into Browser Source</span>
        </div>
        {overlayUrl ? (
          <>
            <div className="dash-share-row" style={{ marginBottom: 10 }}>
              <code>{overlayUrl}</code>
              <CopyButton text={overlayUrl} label="Copy URL" variant="bits" />
            </div>
            <div className="dash-share-row">
              <a href={overlayUrl} target="_blank" rel="noopener noreferrer" className="dash-btn">
                Preview in browser ↗
              </a>
              <a href="/dashboard/overlay" className="dash-btn">Overlay settings</a>
            </div>
          </>
        ) : (
          <p style={{ color: "var(--text-mute)", margin: 0 }}>Overlay key unavailable. Try refreshing.</p>
        )}
      </div>
    </>
  );
}
